import React, { useMemo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, useTheme, Divider } from '@ublend-npm/aulaui-core';
import { toggleStarSpace } from '@core/spaceSelector/operations';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { toggleWidgetPreference } from '@app/actions/home/widgetPreferences';
import { NAVIGATION_SOURCE_STARRED_SPACES } from '@core/constants/analytics';
import { trackHomeViewAllClicked } from '../common';
import {
  getHomeStarredSpaces,
  getHomeSpacesLoading,
  getHomeSpacesErrorType,
  getHomeSpacesForView,
  isHomeViewRoleReady,
} from '@app/selectors/home';
import { fetchHomeSpaces } from '@app/actions/home/fetchHomeSpaces';
import push from '@app/utils/routing';

import { useResponsiveSpacesNavigation } from '@app/hooks/useResponsiveSpacesNavigation';

import SpaceListSkeleton from '../common/SpaceListSkeleton';
import StarredSpaceItem from './StarredSpaceItem';
import { getStarredSpaceColors } from '../../colors';
import StarredSpacesEmptyState from './StarredSpacesEmptyState';
import { WidgetCard, WidgetErrorState } from '../common';
import SpacesListModal from '../../../AllSpaces/SpacesListModal';
import ResponsiveModalPageSwitcher from '../../../common/ResponsiveModalPageSwitcher';
import AllSpacesPage from '../../../AllSpaces/AllSpacesPage';
import useTrackHomeWidgetLoaded from '../common/useTrackHomeWidgetLoaded';

const STRINGS = {
  emptyState: 'No starred spaces.',
  title: 'Starred spaces',
  hide: 'Hide',
  viewAll: 'View all starred spaces',
  widgetKey: 'home-starred-spaces-widget',
  helpArticleUrl:
    'https://aula.zendesk.com/hc/en-us/articles/15852659347218--Starred-Spaces',
};

const ITEM_RENDER_COUNT = 4;

const SKELETON_KEYS = ['skeleton-0', 'skeleton-1', 'skeleton-2'];

const StarredSpacesWidget = () => {
  const { palette } = useTheme();
  const dispatch = useDispatch<any>();
  const { isModalOpen, modalType, openSpacesList, closeAllSpacesModal } =
    useResponsiveSpacesNavigation('starred');

  const isLoading = useSelector(getHomeSpacesLoading);
  const errorType = useSelector(getHomeSpacesErrorType);
  const isViewRoleReady = useSelector(isHomeViewRoleReady);
  const rawSpaces = useSelector(getHomeStarredSpaces);
  const allSpaces = useSelector(getHomeSpacesForView);

  const spaces = useMemo(() => {
    return rawSpaces.map((s: any) => ({
      id: s.objectId,
      name: s.name,
      startDate: s.startDate,
      endDate: s.endDate,
      avatar: s.avatar || null,
    }));
  }, [rawSpaces]);

  useTrackHomeWidgetLoaded(
    'starred_spaces',
    // classRooms are ready once the home view role is ready; do not gate on
    // getHomeSpacesLoading (defaults false) or student-only users fire at 0.
    isViewRoleReady && !errorType,
    spaces.length
  );

  const handleHideWidget = () => {
    dispatch(toggleWidgetPreference(STRINGS.widgetKey, true));
  };

  const handleUnstar = useCallback(
    (id: string) => {
      dispatch(toggleStarSpace({ spaceId: id }));
    },
    [dispatch]
  );

  const handleSpaceClick = useCallback((id: string) => {
    push(`/dashboard/${id}/myspacenav`);
  }, []);

  const handleRetry = useCallback(() => {
    dispatch(fetchHomeSpaces() as any);
  }, [dispatch]);

  const content = useMemo(() => {
    if (isLoading) {
      return SKELETON_KEYS.map((key, index) => (
        <React.Fragment key={key}>
          <SpaceListSkeleton />
          {index < 2 && <Divider />}
        </React.Fragment>
      ));
    }

    if (errorType) {
      return (
        <WidgetErrorState
          type={errorType === 'network' ? 'offline' : errorType}
          onRetry={handleRetry}
          isRetrying={isLoading}
        />
      );
    }

    if (spaces.length === 0) {
      return (
        <StarredSpacesEmptyState
          helpArticleUrl={STRINGS.helpArticleUrl}
          onTrigger={
            allSpaces.length > 0 ? () => openSpacesList('all') : undefined
          }
        />
      );
    }

    const visibleSpaces = spaces.slice(0, ITEM_RENDER_COUNT);

    return visibleSpaces.map((space, index) => (
      <React.Fragment key={space.id}>
        <StarredSpaceItem
          space={space}
          onUnstar={handleUnstar}
          onClick={handleSpaceClick}
        />
        {index < visibleSpaces.length - 1 && <Divider />}
      </React.Fragment>
    ));
  }, [
    isLoading,
    errorType,
    spaces,
    handleUnstar,
    handleSpaceClick,
    openSpacesList,
    handleRetry,
    allSpaces.length,
  ]);

  const menuActions = [{ label: STRINGS.hide, onClick: handleHideWidget }];

  const footer = (
    <Button
      fullWidth
      kind="secondary"
      endIcon={<ArrowForwardIcon />}
      sx={{
        width: '100%',
        color: getStarredSpaceColors(palette).starIcon,
      }}
      onClick={() => {
        trackHomeViewAllClicked(
          NAVIGATION_SOURCE_STARRED_SPACES,
          spaces.length
        );
        openSpacesList('starred');
      }}
    >
      {STRINGS.viewAll}
    </Button>
  );

  const footerUI = spaces.length > ITEM_RENDER_COUNT ? footer : null;

  return (
    <>
      <WidgetCard
        title={STRINGS.title}
        menuActions={menuActions}
        footer={footerUI}
      >
        {content}
      </WidgetCard>

      <ResponsiveModalPageSwitcher
        isOpen={isModalOpen}
        onClose={closeAllSpacesModal}
        modalComponent={
          <SpacesListModal
            isOpen={isModalOpen}
            onClose={closeAllSpacesModal}
            type={modalType}
          />
        }
        pageComponent={
          <AllSpacesPage onClose={closeAllSpacesModal} filterType={modalType} />
        }
      />
    </>
  );
};

export default StarredSpacesWidget;
